import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Bundle, BundleEntry, ChargeItemDefinition, CodeSystem, FhirResource } from 'fhir/r4';
import { DynamicFormsV2Component } from '../../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { formMetaData } from '../../shared/dynamic-forms.interface';
import {
  IndividualField,
  CodeField,
  SingleCodeField,
  GroupField,
  CodeableConceptField
} from '../../shared/dynamic-forms.interface2';
import { FormFieldsSelectDataService } from '../../shared/form-fields-select-data.service';
import { ErrorService } from '../../shared/error.service';
import { StateService } from '../../shared/state.service';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../../app.config';
import { SuccessMessageComponent } from '../../shared/success-message/success-message.component';
import { catchError, forkJoin, map, of, switchMap, take } from 'rxjs';
import { InfoDialogService } from '../../shared/info-dialog/info-dialog.service';

type FormFields = IndividualField | GroupField | CodeField | SingleCodeField | CodeableConceptField;

@Component({
  selector: 'app-add-charges',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    DynamicFormsV2Component
  ],
  templateUrl: './add-charges.component.html',
  styleUrls: ['./add-charges.component.scss']
})
export class AddChargesComponent implements OnInit {
  private formFieldsDataService = inject(FormFieldsSelectDataService);
  private errorService = inject(ErrorService);
  private stateService = inject(StateService);
  private snackBar = inject(MatSnackBar);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  private backendUrl = inject(backendEndPointToken);

  loading = false;
  formMetaData?: formMetaData;
  formFields: FormFields[] = []; // ✅ Initialize as empty array to avoid undefined
  existingChargeId?: string;

  private findActiveDuplicateByServiceCode(service: ChargeItemDefinition) {
    const searchParams = service.code?.coding?.[0]?.code || service.instance?.[0]?.reference?.split('/')[1] || '';
    const system = service.code?.coding?.[0]?.system || undefined;

    // FHIR search: ChargeItemDefinition?code=<code>&status=active
    // Some servers may ignore status filtering; we also filter client-side.
    const url = `${this.backendUrl}/ChargeItemDefinition?status=active&_count=400`;
    return this.http.get<Bundle<ChargeItemDefinition>>(url).pipe(
      map((b: any) => (b?.entry || []).map((e: any) => e?.resource).filter(Boolean).filter((f: ChargeItemDefinition) => {
        //fallback filtyer - active status & .CODE MATCH
        const statusOk = ((f?.status ?? '').toString().trim().toLowerCase() === 'active');
        if (!statusOk) return false;

        const codings: any[] = Array.isArray(f?.code?.coding) ? f.code.coding : [];
        const codeMatch = codings.some((c: any) => (c?.code ?? '').toString().trim() === searchParams) || (f.instance?.[0]?.reference?.split('/')[1] ?? '').toString().trim() === searchParams;
        if (!codeMatch) return false;

        // If system is known, enforce system match; otherwise accept any system for that code.
        if (!system) return true;

        return codings.some(
          (c: any) =>
            (c?.code ?? '').toString().trim() === searchParams &&
            (c?.system ?? '').toString().trim() === system
        );
      }) as ChargeItemDefinition[]),
      // map((defs: ChargeItemDefinition[]) =>
      //   (defs || []).filter((d: any) => {
      //     const statusOk = ((d?.status || '').toString().toLowerCase() === 'active');
      //     if (!statusOk) return false;

      //     const codings: any[] = Array.isArray(d?.code?.coding) ? d.code.coding : [];
      //     const codeMatch = codings.some((c: any) => (c?.code ?? '').toString().trim() === code);
      //     if (!codeMatch) return false;

      //     // If system is known, prefer exact system match. Otherwise accept any system for that code.
      //     if (!system) return true;
      //     return codings.some((c: any) =>
      //       (c?.code ?? '').toString().trim() === code && (c?.system ?? '').toString().trim() === system
      //     );
      //   })
      // ),
      catchError(() => of([] as ChargeItemDefinition[]))
    );
  }

  ngOnInit(): void {
    this.loading = true;

    // Check if editing existing charge
    this.route.queryParams.subscribe(params => {
      this.existingChargeId = params['id'];
      if (this.existingChargeId) {
        this.loadExistingCharge(this.existingChargeId);
      } else {
        this.initializeForm();
      }
    });
  }

  private loadExistingCharge(id: string): void {
    this.http.get<ChargeItemDefinition>(`${this.backendUrl}/ChargeItemDefinition/${id}`)
      .subscribe({
        next: (charge) => {
          console.log('✅ Loaded existing charge:', charge);
          this.initializeForm(charge);
        },
        error: (err) => {
          console.error('❌ Error loading charge:', err);
          this.errorService.openandCloseError('Failed to load charge for editing');
          this.initializeForm();
        }
      });
  }

  terminologyServer = inject(terminologyCanonicalUrlBaseToken);
  private initializeForm(existingCharge?: ChargeItemDefinition): void {
    // Load select data for dropdowns
    forkJoin({
      currency: this.formFieldsDataService.getFormFieldSelectData('chargeItemDef', 'currency'),
      medication: this.http.get<Bundle>(`${this.backendUrl}/Medication?_count=1000`).pipe(
        // Map to reference strings
        map((res: Bundle) => res.entry?.filter((f: any) => f.resource?.status === 'active').map((e: any) => {
          const med = e.resource;
          return {
            reference: `${med.resourceType}/${med.id}`,
            display: `${med.code?.coding?.[0]?.display || med.code?.text || 'Unnamed Medication'}`,
            type: `${med.resourceType}`
          };
          // ${med.resourceType}/${med.id}$#$${med.code?.coding?.[0]?.display || 'Unnamed Medication'}$#$${med.resourceType}`;
        }) || [])
      ),
      service: this.http.get<Bundle>(`${this.backendUrl}/CodeSystem?url=${this.terminologyServer}/CodeSystem/ServiceRequest/ServiceRequestCodes`).pipe(
        // Map to coding strings
        map((res: Bundle) => {

          return (res.entry?.[0]?.resource as CodeSystem)?.concept?.filter(e => {
            return e.property?.some((f: any) => f.code === 'active' && f.valueBoolean)
          }).map((e: any) => {


            return {
              code: e.code,
              display: e.display,
              system: (res.entry?.[0]?.resource as CodeSystem)?.url
            }
          }) || [];
        })
      )
    }).subscribe({
      next: (data) => {
        this.formMetaData = {
          formName: existingCharge ? 'Edit Charge Item Definition' : 'Add Charge Item Definition',
          formDescription: existingCharge
            ? `Update charge: ${existingCharge.title}`
            : 'Define a new billable charge for services, procedures, or products',
          submitText: existingCharge ? 'Update Charge' : 'Create Charge'
        };

        this.formFields = [
          <IndividualField>{
            generalProperties: {
              fieldApiName: 'title',
              fieldName: 'Charge Title',
              fieldLabel: 'Charge Title',
              fieldPlaceholder: 'e.g., Complete Blood Count (CBC)',
              fieldHint: 'Human-readable name for this charge',
              auth: { read: 'all', write: 'admin' },
              isArray: false,
              isGroup: false,
              inputType: 'text',
              value: existingCharge?.title,
              isHidden: true
            }
          },

          <SingleCodeField>{
            generalProperties: {
              fieldApiName: 'status',
              fieldName: 'Status',
              fieldLabel: 'Status',
              fieldType: 'CodeField',
              auth: { read: 'all', write: 'admin' },
              isArray: false,
              isGroup: false,
              value: existingCharge?.status || 'active'
            },
            data: ['draft', 'active', 'retired', 'unknown']
          },
          <IndividualField>{
            generalProperties: {
              fieldApiName: 'url',
              fieldName: 'Machine Identifier code',
              fieldLabel: 'Machine Identifier code',
              fieldPlaceholder: 'child_birth_charge',
              fieldHint: 'Unique canonical URL suffix for this charge definition',
              auth: { read: 'all', write: 'admin' },
              isArray: false,
              isGroup: false,
              inputType: 'url',
              isHidden: true,
              value: existingCharge?.url
            }
          },
          //   <GroupField>{
          //     generalProperties: {
          //       fieldApiName: 'code',
          //       fieldName: 'Billing Code',
          //       fieldLabel: 'Billing Code',
          //       groupFieldsHint: 'Standard billing code (CPT, HCPCS, ICD, etc.)',
          //       fieldType: 'IndividualField',
          //       auth: { read: 'all', write: 'admin' },
          //       isArray: false,
          //       isGroup: true
          //     },
          //     keys: ['code', 'display', 'system'],
          //     groupFields: {
          //       code: <IndividualField>{
          //         generalProperties: {
          //           fieldApiName: 'code',
          //           fieldName: 'Code',
          //           fieldLabel: 'Code',
          //           fieldPlaceholder: 'e.g., 85025',
          //           fieldHint: 'The actual billing code',
          //           auth: { read: 'all', write: 'admin' },
          //           isArray: false,
          //           isGroup: false,
          //           inputType: 'text'
          //         }
          //       },
          //       display: <IndividualField>{
          //         generalProperties: {
          //           fieldApiName: 'display',
          //           fieldName: 'Display Name',
          //           fieldLabel: 'Display Name',
          //           fieldPlaceholder: 'e.g., Complete Blood Count',
          //           fieldHint: 'Human-readable name for the code',
          //           auth: { read: 'all', write: 'admin' },
          //           isArray: false,
          //           isGroup: false,
          //           inputType: 'text'
          //         }
          //       },
          //       system: <IndividualField>{
          //         generalProperties: {
          //           fieldApiName: 'system',
          //           fieldName: 'Code System',
          //           fieldLabel: 'Code System',
          //           fieldPlaceholder: 'e.g., http://www.ama-assn.org/go/cpt',
          //           fieldHint: 'The code system (CPT, ICD-10, SNOMED, etc.)',
          //           auth: { read: 'all', write: 'admin' },
          //           isArray: false,
          //           isGroup: false,
          //           inputType: 'text'
          //         }
          //       }
          //     }
          //   },
          //a field displaying existing services as dropdown
          <GroupField>{
            generalProperties: {
              fieldApiName: 'applicability',
              fieldName: 'Service/Product Selection',
              fieldLabel: 'What is this charge for?',
              groupFieldsHint: 'Specify whether this charge applies to a service or product',
              fieldType: 'IndividualField',
              auth: { read: 'all', write: 'admin' },
              isArray: false,
              isGroup: true
            },
            keys: ['productType', 'service', 'product'],
            groupFields: {
              productType: <SingleCodeField>{
                generalProperties: {
                  fieldApiName: 'productType',
                  fieldName: 'Charge Type',
                  fieldLabel: 'Charge Type',
                  fieldHint: 'Is this charge for a service or a product?',
                  fieldType: 'SingleCodeField',
                  auth: { read: 'all', write: 'admin' },
                  isArray: false,
                  isGroup: false,
                  // value: 'service',
                  controllingField: [
                    {
                      isAControlField: true,
                      dependentFieldVisibilityTriggerValue: 'service',
                      controlledFieldDependencyId: 'service-dependent'
                    },
                    {
                      isAControlField: true,
                      dependentFieldVisibilityTriggerValue: 'product',
                      controlledFieldDependencyId: 'product-dependent'
                    }
                  ]
                },
                data: ['product', 'service']
              },
              service: {
                generalProperties: {
                  fieldApiName: 'service',
                  fieldName: 'Service',
                  fieldLabel: 'Select Service',
                  fieldHint: 'Choose the healthcare service this charge applies to',
                  fieldType: 'CodeableConceptField',
                  auth: { read: 'all', write: 'admin' },
                  isArray: false,
                  isGroup: false,
                  dependence_id: 'service-dependent'
                },
                dataType: 'CodeableConcept',
                data: data.service
              },
              product: <IndividualField>{
                generalProperties: {
                  fieldApiName: 'product',
                  fieldName: 'Product',
                  fieldLabel: 'Select Product/Medication',
                  fieldHint: 'Choose the medication or product this charge applies to',
                  fieldType: 'IndividualReferenceField',
                  auth: { read: 'all', write: 'admin' },
                  isArray: false,
                  isGroup: false,
                  dependence_id: 'product-dependent'
                },
                data: data.medication

              }
            }
          },
          <SingleCodeField>{
            generalProperties: {
              fieldApiName: 'currency',
              fieldName: 'Currency ',
              fieldLabel: 'Currency',
              fieldType: 'CodeableConceptField',
              fieldHint: 'Select the currency for ALL amounts. This is the currency that will be used for all charges',
              auth: { read: 'all', write: 'admin' },
              isArray: false,
              isGroup: false,
              value: 'NGN'
            },
            data: data.currency || ['USD', 'EUR', 'GBP', 'NGN', 'KES', 'ZAR']
          },
          //   <GroupField>{
          //     generalProperties: {
          //       fieldApiName: 'effectivePeriod',
          //       fieldName: 'Effective Period',
          //       fieldLabel: 'Effective Period',
          //       groupFieldsHint: 'Date range when this charge is valid',
          //       fieldType: 'IndividualField',
          //       auth: { read: 'all', write: 'admin' },
          //       isArray: false,
          //       isGroup: true
          //     },
          //     keys: ['start', 'end'],
          //     groupFields: {
          //       start: <IndividualField>{
          //         generalProperties: {
          //           fieldApiName: 'start',
          //           fieldName: 'Start Date',
          //           fieldLabel: 'Start Date',
          //           fieldHint: 'When this charge becomes effective',
          //           auth: { read: 'all', write: 'admin' },
          //           isArray: false,
          //           isGroup: false,
          //           inputType: 'date'
          //         }
          //       },
          //       end: <IndividualField>{
          //         generalProperties: {
          //           fieldApiName: 'end',
          //           fieldName: 'End Date (Optional)',
          //           fieldLabel: 'End Date',
          //           fieldHint: 'When this charge expires (leave empty for ongoing)',
          //           auth: { read: 'all', write: 'admin' },
          //           isArray: false,
          //           isGroup: false,
          //           inputType: 'date'
          //         }
          //       }
          //     }
          //   },
          <GroupField>{
            generalProperties: {
              fieldApiName: 'priceComponent',
              fieldName: 'Price Components',
              fieldLabel: 'Price Components',
              groupFieldsHint: 'Define base price, taxes, surcharges, discounts',
              fieldType: 'IndividualField',
              auth: { read: 'all', write: 'admin' },
              isArray: true,
              isGroup: true
            },
            keys: ['type', 'amount'],
            groupFields: {
              type: <SingleCodeField>{
                generalProperties: {
                  fieldApiName: 'type',
                  fieldName: 'Price Type',
                  fieldLabel: 'Price Type',
                  fieldType: 'CodeField',
                  auth: { read: 'all', write: 'admin' },
                  isArray: false,
                  isGroup: false,
                  value: 'base'
                },
                data: ['base', 'surcharge', 'deduction', 'discount', 'tax', 'informational']
              },
              amount: <IndividualField>{
                generalProperties: {
                  fieldApiName: 'amount',
                  fieldName: 'Amount',
                  fieldLabel: 'Amount',
                  fieldPlaceholder: '0.00',
                  fieldHint: 'Price amount',
                  auth: { read: 'all', write: 'admin' },
                  isArray: false,
                  isGroup: false,
                  inputType: 'number'
                }
              },

            }
          },
          <GroupField>{
            generalProperties: {
              fieldApiName: 'costPriceComponent',
              fieldName: 'Cost Price Components',
              fieldLabel: 'Cost Price Components',
              groupFieldsHint: 'Define base cost, taxes, surcharges, discounts. Only applicable to "Products" ',
              fieldType: 'IndividualField',
              auth: { read: 'all', write: 'admin' },
              isArray: false,
              isGroup: true
            },
            keys: ['amount'],
            groupFields: {
              // type: <SingleCodeField>{
              //   generalProperties: {
              //     fieldApiName: 'type',
              //     fieldName: 'Price Type',
              //     fieldLabel: 'Price Type',
              //     fieldType: 'CodeField',
              //     auth: { read: 'all', write: 'admin' },
              //     isArray: false,
              //     isGroup: false,
              //     value: 'base'
              //   },
              //   data: ['base', 'surcharge', 'deduction', 'discount', 'tax', 'informational']
              // },
              amount: <IndividualField>{
                generalProperties: {
                  fieldApiName: 'amount',
                  fieldName: 'Amount',
                  fieldLabel: 'Amount',
                  fieldPlaceholder: '0.00',
                  fieldHint: 'Price amount',
                  auth: { read: 'all', write: 'admin' },
                  validations: [{
                    type: 'default',
                    isFunction: false,
                    name: 'required'
                  }],
                  isArray: false,
                  isGroup: false,
                  inputType: 'number'
                }
              },
            }
          },

          //


        ];

        this.loading = false;
      },
      error: (err) => {
        console.error('❌ Error loading form data:', err);
        this.errorService.openandCloseError('Error preparing form fields');
        this.loading = false;
      }
    });
  }
  infoDialogService = inject(InfoDialogService);

  onFormSubmit(formData: any): void {
    console.log('📋 Charge Form Submitted:', formData);

    // if (!formData.title) {
    //   this.errorService.openandCloseError('Title is required');
    //   return;
    // }
    if (!formData.applicability || (!formData.applicability.service && !formData.applicability.product)) {
      this.errorService.openandCloseError('Please select a service or product for this charge');
      return;
    }
    if (!formData.priceComponent || formData.priceComponent.length === 0) {
      this.errorService.openandCloseError('At least one price component is required');
      return;
    }
    // if there is service but CostpriceComponent.amiunt is filled, shw notice and continue
    if (formData.applicability?.service && formData.costPriceComponent?.amount) {
      this.infoDialogService.info('Cost Price Component will be ignored for service-based charges.', 6000);
    }
    //get cost price components from form data
    //  [
    //  *   {
    //  *     url: "http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost",
    //  *     extension: [
    //  *       {
    //  *         url: "type",
    //  *         valueCode: "base" | "surcharge" | "deduction" | "discount" | "tax" | "informational"
    //  *       },
    //  *       {
    //  *         url: "code",
    //  *         valueCodeableConcept: {
    //  *           coding: [
    //  *             {
    //  *               system: "http://terminology.hl7.org/CodeSystem/chargeitem-billingcodes",
    //  *               code: "1100",
    //  *               display: "Unvorhergesehene Auslagen"
    //  *             }
    //  *           ]
    //  *         }
    //  *       },
    //  *       {
    //  *         url: "amount",
    //  *         valueMoney: {
    //  *           value: 100.00,
    //  *           currency: "USD"
    //  *         }
    //  *       },
    //  *       {
    //  *         url: "factor",
    //  *         valueDecimal: 1.0
    //  *       }
    //  *     ]
    //  *

    const costPriceComponents = formData.costPriceComponent;
    const pCext = formData.costPriceComponent ? [
      {
        url: "http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost",
        extension: [
          {
            url: "type",
            valueCode: 'base'
          },
          {
            url: "code",
            valueCodeableConcept: {
              text: 'cost price component'
            }
          },
          {
            url: "amount",
            valueMoney: {
              value: parseFloat(costPriceComponents?.amount || '0'),
              currency: formData.currency.code || 'NGN'
            }
          }

        ]
      }
    ] : null;

// $#$    //  /* alert('Cost Price Extension: ' + JSON.stringify(pCext) */);
    const sellingPriceComponents = formData.priceComponent;
    const sellPriceC = sellingPriceComponents?.map((pc: any) => ({
      type: pc.type,
      code: {
        text: 'selling price component'
      },
      amount: {
        value: parseFloat(pc.amount),
        currency: formData.currency.code || 'NGN'
      }
    }));

    // Build FHIR ChargeItemDefinition resource
    const chargeItemDef: ChargeItemDefinition = {
      resourceType: 'ChargeItemDefinition',
      ...(this.existingChargeId ? { id: this.existingChargeId } : {}),
      title: (formData.applicability?.service?.code ||
        formData.applicability?.product?.display ||
        formData.applicability?.product?.reference ||
        'charge_item'
      ).toLowerCase(),
      status: formData.status || 'active',
      description: formData.description,
      url: //form a distinc url here partly using the title
        formData.url ||
        `${this.backendUrl}/ChargeItemDefinition/${(formData.applicability?.service?.code ||
          formData.applicability?.product?.display ||
          formData.applicability?.product?.reference ||
          'charge_item'
        ).toLowerCase().replace(/\s+/g, '_')}${new Date().getTime()}`,
      // effectivePeriod: formData.effectivePeriod,
      ...(formData.applicability?.service ? {
        code: {
          coding: [{
            code: formData.applicability?.service.code,
            display: formData.applicability?.service.display,
            system: formData.applicability?.service.system
          }],
          text: formData.applicability?.service.display
        }
      } : {}
      ),
      propertyGroup: formData.priceComponent ? [{
        priceComponent: sellPriceC
      }] : [],
      ...(formData.applicability?.product ? {
        instance: [{
          reference: formData.applicability?.product.reference,
          display: formData.applicability?.product.display,
          type: formData.applicability?.product.type
        }]
      } : {})
    }

    if (pCext) {
      chargeItemDef.extension = pCext;
    }
    console.log('🔥 FHIR ChargeItemDefinition:', chargeItemDef);

    // Duplicate check (active) for service-based charges.
    // If editing, ignore the current record by id.
    this.loading = true;
    this.findActiveDuplicateByServiceCode(chargeItemDef).pipe(
      take(1),
      switchMap((matches: ChargeItemDefinition[]) => {
        const others = (matches || []).filter((d: any) => {
          if (!this.existingChargeId) return true;
          return (d?.id ?? '') !== this.existingChargeId;
        });

        if (others.length) {
          const dup = others[0] as any;
          const dupTitle = (dup?.title || dup?.name || dup?.description || 'an existing charge').toString();
          this.loading = false;
          this.errorService.openandCloseError(
            `An ACTIVE ChargeItemDefinition already exists for this service code (${formData?.applicability?.service?.code}). (${dupTitle}). Retire it first or edit the existing one.`
          );
          return of(null);
        }

        // Submit to server
        const request = this.existingChargeId
          ? this.http.put<ChargeItemDefinition>(`${this.backendUrl}/ChargeItemDefinition/${this.existingChargeId}`, chargeItemDef)
          : this.http.post<ChargeItemDefinition>(`${this.backendUrl}/ChargeItemDefinition`, chargeItemDef, {
            headers: { 'Content-Type': 'application/fhir+json', Accept: 'application/fhir+json', Prefer: 'return=representation' }
          });
        return request;
      })
    ).subscribe({
      next: (response) => {
        if (!response) return; // duplicate blocked
        console.log('✅ Charge created/updated successfully:', response);
        this.stateService.persistOrgWideResource(response, 'saved');

        this.snackBar.openFromComponent(SuccessMessageComponent, {
          data: { message: this.existingChargeId ? 'Charge updated successfully' : 'Charge created successfully' },
          duration: 3000
        });

        this.router.navigate(['/app/charges']);
      },
      error: (err) => {
        console.error('❌ Error submitting charge:', err);
        this.errorService.openandCloseError('Failed to submit charge. Please try again.');
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  /**
 * Separate total cost price components from ChargeItemDefinition extension
 * Extracts price components from the totalCost extension and maps them to form-friendly format
 * 
 * Extension structure:
 * extension: [{
 *   url: "http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost",
 *   extension: [
 *     { url: "type", valueCode: "base" | "surcharge" | "deduction" | "discount" | "tax" | "informational" },
 *     { url: "code", valueCodeableConcept: {...} },
 *     { url: "amount", valueMoney: { value: 100.00, currency: "USD" } },
 *     { url: "factor", valueDecimal: 1.0 }
 *   ]
 * }]
 * 
 * @param chargeItemDef - ChargeItemDefinition resource with extension
 * @returns Array of price component objects for form fields
 */

}
